import axios from 'axios';
import one360 from './one360';
import {
  HubSpotDealsResponse,
  HubSpotDeal,
  DealContactListItem,
} from '../../types/hubspot';

const DEALS_PAGE_TIMEOUT_MS = 20_000;
const DEALS_SEARCH_TIMEOUT_MS = 65_000;

import type { GetDealsOptions } from '../../interfaces/deals';

// One360 gibt Deals mit verschachtelten `properties: { dealname, dealstage, ... }` zurück.
// Das Frontend erwartet diese Felder flach auf Deal-Ebene → hier mappen.
function flattenDeal(raw: any): HubSpotDeal {
  const { id, properties, createdAt, updatedAt, archived, ...rest } = raw ?? {};
  return { id, createdAt, updatedAt, archived, ...rest, ...(properties ?? {}) } as HubSpotDeal;
}

export async function getDealsAsync(options?: GetDealsOptions): Promise<HubSpotDealsResponse> {
  const params: Record<string, string> = {};
  if (options?.refresh) params['refresh'] = '1';
  if (options?.refreshNacharbeiten) params['refreshNacharbeiten'] = '1';
  if (typeof options?.limit === 'number') params['limit'] = String(options.limit);
  if (options?.after) params['after'] = options.after;
  if (options?.q?.trim()) params['q'] = options.q.trim();
  if (options?.stageLabel?.trim()) params['stageLabel'] = options.stageLabel.trim();
  if (options?.nacharbeiten === 'yes' || options?.nacharbeiten === 'no') params['nacharbeiten'] = options.nacharbeiten;

  const isSearchRequest = Boolean(options?.q && options.q.trim().length >= 3);
  const timeoutMs = isSearchRequest ? DEALS_SEARCH_TIMEOUT_MS : DEALS_PAGE_TIMEOUT_MS;

  const headers = options?.refresh || options?.bypassCache ? { 'X-One360-No-Cache': 'true' } : undefined;

  try {
    const raw = await one360.get<{ deals: any[]; after?: string; hasMore?: boolean }>(
      '/api/v1/hubspot/deals',
      { params, headers, timeout: timeoutMs, signal: options?.signal }
    );
    return {
      deals: (raw.deals ?? []).map(flattenDeal),
      after: raw.after,
      hasMore: raw.hasMore,
    };
  } catch (err: any) {
    if (axios.isCancel(err)) throw err;
    console.error('[HubSpot] getDeals fehlgeschlagen:', err);
    throw new Error('Deals konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function getDealByIdAsync(dealId: string, options?: { bypassCache?: boolean }): Promise<HubSpotDeal | null> {
  const id = dealId.trim();
  if (!id) return null;
  const headers = options?.bypassCache ? { 'X-One360-No-Cache': 'true' } : undefined;
  try {
    const raw = await one360.get<any>(`/api/v1/hubspot/deals/${encodeURIComponent(id)}`, { headers });
    return flattenDeal(raw);
  } catch (err: any) {
    console.error('[HubSpot] getDealById fehlgeschlagen:', err);
    return null;
  }
}

export async function getDealContactsAsync(dealId: string, options?: { bypassCache?: boolean }): Promise<DealContactListItem[]> {
  const id = dealId.trim();
  if (!id) return [];
  const headers = options?.bypassCache ? { 'X-One360-No-Cache': 'true' } : undefined;
  try {
    const result = await one360.get<DealContactListItem[] | { contacts: DealContactListItem[] }>(
      `/api/v1/hubspot/deals/${encodeURIComponent(id)}/contacts`,
      { headers }
    );
    return Array.isArray(result) ? result : result?.contacts ?? [];
  } catch (err: any) {
    console.error('[HubSpot] getDealContacts fehlgeschlagen:', err);
    return [];
  }
}

export async function uploadDocumentAsync(
  dealId: string,
  propertyName: string,
  file: File,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const result = await one360.post<{ fileId?: string; id?: string }>(
      `/api/v1/hubspot/deals/${encodeURIComponent(dealId)}/documents/${encodeURIComponent(propertyName)}`,
      formData,
    );
    return result?.fileId ?? result?.id ?? '';
  } catch (err: any) {
    console.error('[HubSpot] uploadDocument fehlgeschlagen:', err);
    throw new Error('Die Datei konnte nicht hochgeladen werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function resolveOwnersByEmailAsync(email: string): Promise<string[]> {
  const normalizedEmail = email.trim();
  if (!normalizedEmail) return [];
  try {
    const response = await one360.get<{ ownerIds?: string[] }>('/api/v1/hubspot/owners/resolve', {
      params: { email: normalizedEmail },
    });
    const ownerIds = Array.isArray(response.ownerIds) ? response.ownerIds : [];
    return ownerIds.map((id) => String(id || '').trim()).filter(Boolean);
  } catch (err: any) {
    console.error('[HubSpot] resolveOwners fehlgeschlagen:', err);
    return [];
  }
}

// HubSpot file IDs are numeric; Google Drive IDs are alphanumeric (33+ chars)
function isHubSpotFileId(fileId: string): boolean {
  return /^\d+$/.test(fileId.trim());
}

export async function getSignedUrlAsync(fileId: string): Promise<string> {
  try {
    if (isHubSpotFileId(fileId)) {
      const response = await one360.get<{ url: string }>(`/api/v1/hubspot/files/${encodeURIComponent(fileId)}/signed-url`);
      return response.url;
    }
    const response = await one360.get<{ url: string }>(`/api/v1/google/drive/link/${encodeURIComponent(fileId)}`);
    return response.url;
  } catch (err: any) {
    console.error('[HubSpot] getSignedUrl fehlgeschlagen:', err);
    throw new Error('Die Datei konnte nicht abgerufen werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function updateTextPropertyAsync(dealId: string, property: string, value: string): Promise<void> {
  try {
    await one360.patch(`/api/v1/hubspot/deals/${encodeURIComponent(dealId)}/properties`, { Properties: { [property]: value } });
  } catch (err: any) {
    console.error('[HubSpot] updateTextProperty fehlgeschlagen:', err);
    throw new Error('Die Eigenschaft konnte nicht gespeichert werden.');
  }
}

export async function searchDealsAsync(query: string, options?: { signal?: AbortSignal }): Promise<HubSpotDeal[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  try {
    const response = await getDealsAsync({ q, signal: options?.signal });
    return response.deals;
  } catch (err: any) {
    if (axios.isCancel(err)) throw err;
    const msg = (err?.message ?? '').toLowerCase();
    if (msg.includes('abort') || msg.includes('cancel')) throw err;
    console.error('[HubSpot] searchDeals fehlgeschlagen:', err);
    throw new Error('Deal-Suche fehlgeschlagen.');
  }
}

export async function getNacharbeitenStatusAsync(projectId: string): Promise<import('../../types/hubspot').NacharbeitenStatusSnapshot> {
  try {
    return await one360.get(`/api/v1/ops/projects/${encodeURIComponent(projectId)}/nacharbeiten-status`);
  } catch (err: any) {
    console.error('[HubSpot] getNacharbeitenStatus fehlgeschlagen:', err);
    throw new Error('Nacharbeiten-Status konnte nicht geladen werden.');
  }
}

export async function getNacharbeitenTicketsAsync(dealId: string): Promise<import('../../types/hubspot').NacharbeitenTicketListItem[]> {
  try {
    return await one360.get(`/api/v1/ops/projects/${encodeURIComponent(dealId)}/nacharbeiten-tickets`);
  } catch (err: any) {
    console.error('[HubSpot] getNacharbeitenTickets fehlgeschlagen:', err);
    throw new Error('Nacharbeiten-Tickets konnten nicht geladen werden.');
  }
}
